import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Calendar, Lock, CalendarPlus, ArrowLeft, ArrowRight, MapPin, Users, Video,
  BookOpen, Clock,
} from 'lucide-react';
import PageHero from '@/components/PageHero';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Workshops — live virtual, in-person, webinars, conferences, community events.
 *  Future-ready fields per workshop: title, description, presenter, date, time, timezone,
 *  location, format (virtual/in-person), capacity, waitlist_count, meeting_link,
 *  registration_url, ce_credits, premium. Falls back to a polished empty state when none scheduled. */
export default function Workshops() {
  const { user } = useAuth();
  const [workshops, setWorkshops] = useState(null); // null=loading, [] = empty
  useEffect(() => {
    http.get('/events').then(r => setWorkshops(r.data)).catch(() => setWorkshops([]));
  }, []);

  const register = async (id, premium) => {
    if (premium && !user?.is_premium) { toast.error('Members-only workshop — join Brotherhood+ to attend'); return; }
    try {
      await http.post(`/events/${id}/register`);
      toast.success('You\'re registered');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not register');
    }
  };

  const addToCalendar = (id, title) => {
    const link = document.createElement('a');
    link.href = `${API_URL}/api/events/${id}/ical`;
    link.download = `${id}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Downloading calendar file for "${title}"`);
  };

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-5 lg:space-y-8" data-testid="workshops-page">
      <Link to="/app" data-testid="workshops-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
        <ArrowLeft size={14}/> Back to home
      </Link>
      <PageHero photo="workshops" alt="A Black man engaged in community learning" />
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Workshops</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-1 lg:mt-2">Live learning, together.</h1>
        <p className="hidden lg:block text-navy/60 mt-3 max-w-2xl">
          Register in one tap and add to your calendar. Works with Apple, Google, Outlook, and any RFC-5545 client.
        </p>
      </div>

      {workshops === null && (
        <div className="text-navy/50" data-testid="workshops-loading">Loading workshops…</div>
      )}

      {workshops?.length === 0 && (
        <EmptyWorkshops/>
      )}

      {workshops?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6" data-testid="workshops-grid">
          {workshops.map(w => <WorkshopCard key={w.id} w={w} onRegister={register} onCal={addToCalendar}/>)}
        </div>
      )}

      {workshops?.length > 0 && (
        <p className="text-xs text-navy/40 text-center pt-4">
          Compatible with Apple Calendar, Google Calendar, Outlook, and any RFC-5545 calendar app.
        </p>
      )}
    </div>
  );
}

function EmptyWorkshops() {
  return (
    <div className="bg-white rounded-3xl p-8 lg:p-12 card-soft text-center max-w-2xl mx-auto" data-testid="workshops-empty">
      <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto rounded-full bg-beige flex items-center justify-center mb-5">
        <Calendar size={28} className="text-navy/70"/>
      </div>
      <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold">Workshops</p>
      <h2 className="font-heading text-2xl lg:text-3xl leading-snug mt-3">No workshops are currently scheduled.</h2>
      <p className="text-navy/60 text-sm lg:text-base mt-4 leading-relaxed">
        When new live workshops become available, they&apos;ll appear here. You can still continue
        learning through the Academy, AI Coach, and Growth Plans.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        <Link to="/app/learn" data-testid="workshops-empty-learn-cta"
          className="bg-navy text-white rounded-2xl px-4 py-3 text-sm font-medium hover:bg-navy-soft transition-smooth inline-flex items-center justify-center gap-2">
          <BookOpen size={14}/> Browse Brotherhood University
        </Link>
        <Link to="/app/coach" data-testid="workshops-empty-coach-cta"
          className="bg-beige text-navy rounded-2xl px-4 py-3 text-sm font-medium hover:bg-[#EAE5D9] transition-smooth inline-flex items-center justify-center gap-2">
          AI Coach <ArrowRight size={14}/>
        </Link>
        <Link to="/app/plans" data-testid="workshops-empty-plans-cta"
          className="bg-beige text-navy rounded-2xl px-4 py-3 text-sm font-medium hover:bg-[#EAE5D9] transition-smooth inline-flex items-center justify-center gap-2">
          Growth Plans <ArrowRight size={14}/>
        </Link>
      </div>
    </div>
  );
}

function WorkshopCard({ w, onRegister, onCal }) {
  const formatLabel = w.format || (w.location?.toLowerCase()?.includes('virtual') ? 'Virtual' : (w.location ? 'In-person' : 'Virtual'));
  const capacityUsed = typeof w.registered_count === 'number' && typeof w.capacity === 'number';
  const capacityFull = capacityUsed && w.registered_count >= w.capacity;

  return (
    <div className="bg-white rounded-3xl p-6 lg:p-7 card-soft" data-testid={`workshop-${w.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] lg:text-xs uppercase tracking-widest text-navy/50 font-semibold">
            {w.type || 'Workshop'}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] lg:text-xs uppercase tracking-widest text-navy/50 bg-beige rounded-full px-2 py-0.5">
            {formatLabel === 'Virtual' ? <Video size={10}/> : <MapPin size={10}/>}
            {formatLabel}
          </span>
        </div>
        {w.premium && <Lock size={14} className="text-gold"/>}
      </div>

      <p className="font-heading text-xl lg:text-2xl mt-2">{w.title}</p>
      {w.description && (
        <p className="text-sm lg:text-base text-navy/60 mt-1 leading-relaxed">{w.description}</p>
      )}
      {w.presenter && (
        <p className="text-xs lg:text-sm text-navy/70 mt-2">
          <span className="uppercase tracking-widest text-navy/40">Presented by</span> · {w.presenter}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs lg:text-sm text-navy/60">
        <span className="inline-flex items-center gap-1.5"><Calendar size={12}/>{w.date}</span>
        <span className="inline-flex items-center gap-1.5"><Clock size={12}/>{w.time}{w.timezone ? ` · ${w.timezone}` : ''}</span>
        {w.location && !w.location.toLowerCase().includes('virtual') && (
          <span className="inline-flex items-center gap-1.5"><MapPin size={12}/>{w.location}</span>
        )}
        {capacityUsed && (
          <span className="inline-flex items-center gap-1.5"><Users size={12}/>{w.registered_count}/{w.capacity} registered</span>
        )}
        {w.ce_credits && (
          <span className="inline-flex items-center gap-1.5 text-gold font-medium">{w.ce_credits} CE credits</span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {!capacityFull && (
          <button data-testid={`workshop-register-${w.id}`} onClick={()=>onRegister(w.id, w.premium)}
            className="bg-navy text-white text-xs lg:text-sm rounded-full px-4 py-2 lg:px-5 lg:py-2.5 hover:bg-navy-soft transition-smooth">
            Register
          </button>
        )}
        {capacityFull && (
          <button data-testid={`workshop-waitlist-${w.id}`} onClick={()=>onRegister(w.id, w.premium)}
            className="bg-beige text-navy text-xs lg:text-sm rounded-full px-4 py-2 lg:px-5 lg:py-2.5 hover:bg-[#EAE5D9] transition-smooth">
            Join waitlist
          </button>
        )}
        <button data-testid={`workshop-ical-${w.id}`} onClick={()=>onCal(w.id, w.title)}
          className="bg-beige text-navy text-xs lg:text-sm rounded-full px-4 py-2 lg:px-5 lg:py-2.5 hover:bg-[#EAE5D9] transition-smooth inline-flex items-center gap-2">
          <CalendarPlus size={14}/> Add to Calendar
        </button>
        {w.meeting_link && formatLabel === 'Virtual' && (
          <a href={w.meeting_link} target="_blank" rel="noopener noreferrer"
            data-testid={`workshop-join-${w.id}`}
            className="text-navy text-xs lg:text-sm rounded-full px-4 py-2 lg:px-5 lg:py-2.5 hover:bg-beige transition-smooth inline-flex items-center gap-2 border border-navy/15">
            <Video size={14}/> Join link
          </a>
        )}
      </div>
    </div>
  );
}
